/* =============================================================================
   Registry - the component catalog. A component registers its name, its
   constructor and, when it auto-mounts, the selector it mounts on.
   The facade generator reads the catalog to build the $.fn.ics* plugin surface,
   and Ics.mount() iterates mountable().
   RULE: a lifecycle re-scan always targets THESE registered selectors, never a
   broad class sweep - that is what stops a re-scan from mounting elements this
   library does not own.
   Registering the same name twice warns and OVERWRITES the earlier entry.
   It is also the introspection point for harnesses and the runtime probe.
   API: add(name, Ctor[, opts]) · get(name) · all() · mountable().
   ============================================================================= */

const _byName = new Map();

export const Registry = {
  /**
   * Register a component.
   * @param {string} name       PascalCase widget name ('Datebox' -> $.fn.icsDatebox)
   * @param {Function} Ctor     Component subclass
   * @param {object} [opts]
   * @param {string} [opts.selector]  auto-mount selector (e.g. '[data-ics="datebox"], .ics-enhance-datebox')
   * @param {boolean} [opts.facade=true]  expose as $.fn.ics<name>
   * @param {string} [opts.claimName]  ALSO expose the component under this
   *   shorter $.fn call name (e.g. 'datebox'), but only where the name is free.
   *   Rule: claim a short name only where this component IS the 1:1 owner of it,
   *   and never shadow a name another library on the page already installs.
   */
  add(name, Ctor, opts = {}) {
    if (_byName.has(name)) console.warn('[ics] Registry: duplicate registration', name);
    _byName.set(name, {
      name, Ctor,
      selector: opts.selector || null,
      facade: opts.facade !== false,
      claimName: opts.claimName || null,
    });
    return Ctor;
  },

  get(name) { return _byName.get(name) || null; },

  /** every entry (facade generation, harness introspection) */
  all() { return Array.from(_byName.values()); },

  /** entries that auto-mount by selector (Ics.mount drives off this) */
  mountable() { return this.all().filter(e => !!e.selector); },
};
